import { createReducer } from "typesafe-actions";

import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";
import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";

import AnnouncementsTrigger from "@app/types/AnnouncementsTrigger";

import {
  Actions,
  endStartupWait,
  fetchAnnouncements,
  hideAnnouncements,
  openAnnouncements,
  postponeAnnouncement,
  resetAnnouncements,
  showNextAnnouncement,
  skipAnnouncements,
  suppressAnnouncements,
  triggerAnnouncements,
} from "./actions";

export interface AnnouncementsState {
  announcements: Announcement[];
  currentIndex: number;
  hiddenForSession: boolean; // Set by the X or a modal render error until a reload or sign-out, and for the whole session of a tab an announcement CTA opened.
  loaded: boolean; // A fetch has succeeded in this session.
  loading: boolean;
  modalPageId: AnnouncementPageId | undefined; // The page the open modal was opened on, frozen like the queue.
  modalTrigger: AnnouncementsTrigger | undefined; // Why the open modal was opened.
  open: boolean;
  pendingTrigger: AnnouncementsTrigger | undefined; // Set when app start or a page change asks to show announcements; the gate opens the modal or skips.
  postponedIds: Announcement["id"][]; // Session-only: nothing persists it, so a page reload brings postponed announcements back.
  queue: Announcement[];
  startupWaitOver: boolean; // Auto-running guides wait until the session's first fetch settles or takes too long.
  viewedIds: Announcement["id"][];
}

const initialState: AnnouncementsState = {
  announcements: [],
  currentIndex: 0,
  hiddenForSession: false,
  loaded: false,
  loading: false,
  modalPageId: undefined,
  modalTrigger: undefined,
  open: false,
  pendingTrigger: undefined,
  postponedIds: [],
  queue: [],
  startupWaitOver: false,
  viewedIds: [],
};

const closed = (state: AnnouncementsState): AnnouncementsState => ({
  ...state,
  currentIndex: 0,
  modalPageId: undefined,
  modalTrigger: undefined,
  open: false,
  queue: [],
});

const advance = (state: AnnouncementsState): AnnouncementsState =>
  state.currentIndex + 1 >= state.queue.length
    ? closed(state)
    : { ...state, currentIndex: state.currentIndex + 1 };

const announcements = createReducer<AnnouncementsState, Actions>(initialState)
  .handleAction(fetchAnnouncements.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(fetchAnnouncements.success, (state, { payload }) => ({
    ...state,
    announcements: payload,
    loaded: true,
    loading: false,
    // Only the first success of a session shows anything, as a startup request even over a pending
    // page change; a refresh waits for a page change.
    pendingTrigger: state.loaded ? state.pendingTrigger : "startup",
  }))
  .handleAction(fetchAnnouncements.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(openAnnouncements, (state, { payload: { pageId, queue, trigger } }) => ({
    ...state,
    currentIndex: 0,
    modalPageId: pageId,
    modalTrigger: trigger,
    open: true,
    pendingTrigger: undefined,
    queue: [...queue], // A new array per open, so the queue identifies one modal open.
  }))
  .handleAction(triggerAnnouncements, (state) =>
    state.open ? state : { ...state, pendingTrigger: "navigation" }
  )
  .handleAction(skipAnnouncements, (state) => ({ ...state, pendingTrigger: undefined }))
  .handleAction(suppressAnnouncements, (state) => ({
    ...closed(state),
    hiddenForSession: true,
    startupWaitOver: true,
  }))
  .handleAction(endStartupWait, (state) => ({ ...state, startupWaitOver: true }))
  .handleAction(showNextAnnouncement, (state, { payload }) =>
    advance({ ...state, viewedIds: [...state.viewedIds, payload] })
  )
  .handleAction(postponeAnnouncement, (state, { payload }) =>
    advance({ ...state, postponedIds: [...state.postponedIds, payload] })
  )
  .handleAction(hideAnnouncements, (state) => ({ ...closed(state), hiddenForSession: true }))
  .handleAction(resetAnnouncements, () => initialState);

export * from "./selectors";

export type AnnouncementsActions = Actions;

export default announcements;
